<template>
  <section class="cta dark" id="cta">
    <div class="container">
      <div class="row">
        <div class="col-md-6">
          <h5 class="text-white">{{ t('Any questions?') }}</h5>
          <h1 class="text-white mb-4">{{ t('Get In Touch') }}</h1>
          <p class="mb-5">{{ t('Our team is more than happy to assist you. You can simply leave us a message and we will contact you as soon as possible.') }}</p>
          <ul class="fa-ul">
            <li>
              <span class="fa-li">
                <font-awesome-icon icon="envelope" />
              </span>
              <a href="mailto:support@veximus.com.my"> support@veximus.com.my </a>
            </li>
            <li>
              <span class="fa-li"> <font-awesome-icon icon="map-signs" /> </span>
              D-01-01, Menara Mitraland<br />
              13A, Jalan PJU 5/1, Kota Damansara<br />
              47810 Petaling Jaya, Selangor<br />
              <a class="small" href="https://g.page/veximus" target="_blank" rel="noopener">{{ t('(View On Map)') }}</a>
            </li>
            <li>
              <span class="fa-li">
                <font-awesome-icon icon="clock" />
              </span>
              {{ t('Business Hours') }}: 9.30 a.m. to 6.30 p.m.<br />
              {{ t('Closed on Weekends and Public Holidays') }}
            </li>
          </ul>
        </div>
        <div class="col-md-6">
          <form
            @submit.prevent="
              form.post(route('message'), {
                preserveScroll: true,
                onSuccess: () => handleSuccess()
              })
            "
          >
            <div class="mb-3">
              <label class="required">{{ t('Name') }}</label>
              <input type="text" class="form-control" name="name" :placeholder="t('Enter your name...')" v-model="form.name" v-bind:class="{ 'is-invalid': errors.name }" />
              <div class="invalid-feedback" v-show="errors.name">
                {{ errors.name }}
              </div>
            </div>
            <div class="mb-3">
              <label class="required">{{ t('Email address') }}</label>
              <input type="email" class="form-control" name="email" :placeholder="t('Enter your email address...')" v-model="form.email" v-bind:class="{ 'is-invalid': errors.email }" />
              <div class="invalid-feedback" v-show="errors.email">
                {{ errors.email }}
              </div>
            </div>
            <div class="mb-3">
              <label>{{ t('Message') }}</label>
              <textarea class="form-control" name="content" rows="5" :placeholder="t('(Optional) Leave us your message...')" v-model="form.content"></textarea>
            </div>
            <button type="submit" class="btn btn-primary" :disabled="form.processing">
              <font-awesome-icon v-if="!form.processing" icon="paper-plane" class="me-2" size="sm" />
              <font-awesome-icon v-else :icon="['fad', 'spinner-third']" class="me-2" spin />
              {{ t('Send Message') }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.cta {
  background: linear-gradient(90deg, $primary, #00a0ff);
  color: #fff;
  position: relative;

  &::before {
    background: url('/images/ui/hero-bg.png') no-repeat top;
    background-size: cover;
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.5;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  .fa-ul {
    margin-left: 3.5em;

    .fa-li {
      left: -3em;
    }

    a {
      color: #fff;
      text-decoration: underline;
    }

    @media (max-width: 767.98px) {
      margin-bottom: 3rem;
    }
  }

  form {
    position: relative;
    z-index: 5;

    label {
      font-size: 0.9rem;
      font-weight: 500;
      margin-bottom: 0.5rem;
    }

    .invalid-feedback {
      background: #dc3545;
      border-radius: 0.25rem;
      color: #fff;
      display: inline-block;
      padding: 0rem 0.25rem;
      width: auto;
    }
  }
}
</style>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { useI18n } from 'vue-i18n';

export default {
  computed: {
    errors() {
      return this.$page.props.errors;
    }
  },

  setup() {
    const form = useForm({
      name: null,
      email: null,
      content: null
    });

    const { t } = useI18n({
      messages: {
        'en-MY': {
          '(Optional) Leave us your message...': '(Optional) Leave us your message...',
          '(View On Map)': '(View On Map)',
          'Any questions?': 'Any questions?',
          'Business Hours': 'Business Hours',
          'Closed on Weekends and Public Holidays': 'Closed on Weekends and Public Holidays',
          'Email address': 'Email address',
          'Enter your email address...': 'Enter your email address...',
          'Enter your name...': 'Enter your name...',
          'Get In Touch': 'Get In Touch',
          'Our team is more than happy to assist you. You can simply leave us a message and we will contact you as soon as possible.': 'Our team is more than happy to assist you. You can simply leave us a message and we will contact you as soon as possible.',
          'Send Message': 'Send Message',
          Message: 'Message',
          Name: 'Name'
        },
        'zh-MY': {
          '(Optional) Leave us your message...': '（可选）给我们留言...',
          '(View On Map)': '（查看地图）',
          'Any questions?': '还有疑问？',
          'Business Hours': '营业时间',
          'Closed on Weekends and Public Holidays': '周末及公众假期休息',
          'Email address': '电邮地址',
          'Enter your email address...': '输入你的电邮...',
          'Enter your name...': '输入你的名字...',
          'Get In Touch': '马上联系我们',
          'Our team is more than happy to assist you. You can simply leave us a message and we will contact you as soon as possible.': '我们的团队很高兴能够为您服务，您只需要留下您的信息，我们收到以后将会尽快与您取得联系。',
          'Send Message': '发送信息',
          Message: '信息',
          Name: '名称'
        },
        'ms-MY': {
          '(Optional) Leave us your message...': '(Pilihan) Tinggalkan mesej anda...',
          '(View On Map)': '(Peta Lokasi)',
          'Any questions?': 'Ada sebarang pertanyaan?',
          'Business Hours': 'Waktu operasi',
          'Closed on Weekends and Public Holidays': 'Tutup setiap hujung minggu dan cuti umum',
          'Email address': 'Alamat emel',
          'Enter your email address...': 'Masukkan alamat emel anda...',
          'Enter your name...': 'Masukkan nama anda...',
          'Get In Touch': 'Hubungi Kami',
          'Our team is more than happy to assist you. You can simply leave us a message and we will contact you as soon as possible.': 'Kami senang hati dapat membantu anda. Tinggalkan mesej anda di sini, kami akan menghubungi anda secepat mungkin.',
          'Send Message': 'Hantar Mesej',
          Message: 'Mesej',
          Name: 'Nama'
        }
      }
    });

    return { form, t };
  }
};
</script>
